import React from 'react';
import ArcPrototypePage from '../../pages/coach/ArcPrototypePage';
import RowerJourneyPrototypePage from '../../pages/coach/RowerJourneyPrototypePage';
import RowerSidePrototypePage from '../../pages/coach/RowerSidePrototypePage';
import PowerAnalysisD3PrototypePage from '../../pages/coach/PowerAnalysisD3PrototypePage';
import GateForcePrototypePage from '../../pages/coach/GateForcePrototypePage';
import PerformanceMetricsD3PrototypePage from '../../pages/coach/PerformanceMetricsD3PrototypePage';
import Concept2WorkoutAnalysisPrototypePage from '../../pages/rower/Concept2WorkoutAnalysisPrototypePage';
import type { VisualizationVersion } from '../../platform/visualizationVersion';
import { UserRole } from '../../types/database';

export type PrototypeDataSource = 'session' | 'concept2-workout';

export type PrototypeRendererProps = {
  sessionId: string | null;
  concept2WorkoutId: string | null;
};

const ArcPrototypePageWithSession: React.FC<PrototypeRendererProps> = ({ sessionId }) => (
  <ArcPrototypePage sessionId={sessionId} />
);

const Concept2WorkoutAnalysisPrototypePageWithWorkout: React.FC<PrototypeRendererProps> = ({
  concept2WorkoutId,
}) => <Concept2WorkoutAnalysisPrototypePage workoutId={concept2WorkoutId} />;

const createPrototypeWrapper = (
  Component: React.ComponentType
): React.FC<PrototypeRendererProps> => {
  const Wrapped: React.FC<PrototypeRendererProps> = () => <Component />;
  Wrapped.displayName = `PrototypeWrapper(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
};

export interface PrototypeEntry {
  id: string;
  title: string;
  description: string;
  /** Sandbox iteration version for this visualization concept */
  version: VisualizationVersion;
  status: 'In Progress' | 'Needs Review' | 'Ready';
  portal: UserRole;
  component: React.ComponentType<PrototypeRendererProps>;
  dataSource: PrototypeDataSource | null;
  platform: 'D3.js' | 'Three.js' | 'Other';
  /** Production graph registry id when this prototype maps to a shipped chart */
  productionGraphId?: string;
}

export const PROTOTYPE_REGISTRY: PrototypeEntry[] = [
  {
    id: 'rowing-journey',
    title: 'Rower Journey Timeline',
    description:
      'Interactive D3 timeline showing sessions and events across data sources with pan/zoom.',
    version: '1.0.0',
    status: 'In Progress',
    portal: UserRole.Coach,
    component: createPrototypeWrapper(RowerJourneyPrototypePage),
    dataSource: null,
    platform: 'D3.js',
  },
  {
    id: 'rowing-arc',
    title: 'Rowing Arc (Stroke Geometry)',
    description: 'D3 visualization of rowing stroke geometry showing oar angle patterns.',
    version: '1.0.0',
    status: 'In Progress',
    portal: UserRole.Coach,
    component: ArcPrototypePageWithSession,
    dataSource: 'session',
    platform: 'D3.js',
    productionGraphId: 'rowing-arc',
  },
  {
    id: 'rower-side-2',
    title: 'Rower Side Perspective',
    description: 'Animated rower side view exploring biomechanical storytelling concepts.',
    version: '2.0.0',
    status: 'Needs Review',
    portal: UserRole.Coach,
    component: createPrototypeWrapper(RowerSidePrototypePage),
    dataSource: null,
    platform: 'Three.js',
  },
  {
    id: 'power-analysis-d3',
    title: 'Power Analysis D3',
    description:
      'Interactive D3 visualization of power output over time with pan, zoom, and animation controls.',
    version: '2.0.0',
    status: 'In Progress',
    portal: UserRole.Coach,
    component: PowerAnalysisD3PrototypePage,
    dataSource: 'session',
    platform: 'D3.js',
    productionGraphId: 'power-analysis',
  },
  {
    id: 'gate-force',
    title: 'Gate Force Signature',
    description:
      'D3 visualization of gate force patterns showing force application curves for each rower throughout the stroke cycle.',
    version: '3.0.0',
    status: 'In Progress',
    portal: UserRole.Coach,
    component: GateForcePrototypePage,
    dataSource: 'session',
    platform: 'D3.js',
    productionGraphId: 'gate-force',
  },
  {
    id: 'individual-rower-power-d3',
    title: 'Individual Rower Power (D3)',
    description:
      'D3.js migration of the Individual Rower Power chart with horizontal bar overlays for ranges, percentiles, peaks, and density distribution.',
    version: '2.0.0',
    status: 'Ready',
    portal: UserRole.Coach,
    component: PerformanceMetricsD3PrototypePage,
    dataSource: 'session',
    platform: 'D3.js',
    productionGraphId: 'performance-metrics',
  },
  {
    id: 'concept2-workout-analysis',
    title: 'Concept2 Workout Analysis',
    description:
      'ErgData-inspired pace, stroke rate, and heart rate charts for Concept2 Logbook workouts.',
    version: '1.0.0',
    status: 'In Progress',
    portal: UserRole.Athlete,
    component: Concept2WorkoutAnalysisPrototypePageWithWorkout,
    dataSource: 'concept2-workout',
    platform: 'D3.js',
    productionGraphId: 'concept2-workout-analysis',
  },
];

export const getPrototypesForPortal = (portal: UserRole): PrototypeEntry[] =>
  PROTOTYPE_REGISTRY.filter((entry) => entry.portal === portal);

export const findPrototypeById = (id: string, portal?: UserRole): PrototypeEntry | undefined => {
  if (portal) {
    return PROTOTYPE_REGISTRY.find((entry) => entry.id === id && entry.portal === portal);
  }
  return PROTOTYPE_REGISTRY.find((entry) => entry.id === id);
};
